import { AppBar, Tabs, Tab, Button, Grid, Tooltip } from '@mui/material';
import { useQuery } from 'react-query';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Link, Page, PageTitleWithBack, Button as SMButton } from '../../../components';
import {
  getJobGeneralInfo,
  getJobLotInfo,
  getJobSitePrepInfo,
  getJobHouseInfo,
  getUtilityMunicipalInfo,
  getFinanceInfo,
  getContractInfo,
  getContractInfoReadOnly
} from '../../../fetch';
import {
  IJobGeneralInfo,
  IJobLotInfo,
  IJobSitePrepInfo,
  IJobHouseInfo,
  IUtilityMunicipalInfo,
  IFinanceInfo,
  IContractInfo,
  IContractInfoReadOnly
} from '../../../models';
import { GeneralInformation } from './general-information';
import { LotInformation } from './lot-info';
import { SitePrep } from './site-prep';
import { HouseInfo } from './house-info';
import { ExternalLink } from '../../../components/link/ExternalLink';
import { UtilityMunicipalityInfo } from './utility-municipality-info';
import { FinanceInfo } from './finance-info';
import { ContractInfo } from './contract-info';
import { useFlags } from '../../../hooks';
const { LegacySystem } = require('../../../buildSettings.json');

export const enum jobPages {
  genInfo,
  lotInfo,
  sitePrep,
  houseInfo,
  utilityMunicipalInfo,
  financeInfo,
  contractInfo
}
interface IAddEditJob {}

export const AddEditJob: FC<IAddEditJob> = () => {
  const classes = useStyles();
  const [selectedPage, setSelectedPage] = useState<jobPages>(jobPages.genInfo);
  const [selectedTab, setSelectedTab] = useState(window.location.pathname);
  const [isDirtyForm, setDirtyForm] = useState<boolean>(false);
  const { jobId }: { jobId: string } = useParams();
  const history = useHistory();
  const { documentationBuildDocumentation } = useFlags();

  const {
    isLoading: isLoadingGeneralInfo,
    data: generalInfo,
    refetch: refetchGeneralInfo
  } = useQuery<IJobGeneralInfo, Error>(
    ['job-general-info', jobId],
    () => getJobGeneralInfo(jobId),
    {
      //   enabled: jobId !== 'add'
    }
  );

  const {
    isLoading: isLoadingLotInfo,
    data: lotInfo,
    refetch: refetchLotInfo
  } = useQuery<IJobLotInfo, Error>(['job-lot-info', jobId], () => getJobLotInfo(jobId), {
    //   enabled: jobId !== 'add'
  });

  const {
    isLoading: isLoadingSitePrepInfo,
    data: sitePrepInfo,
    refetch: refetchSitePrepInfo
  } = useQuery<IJobSitePrepInfo, Error>(
    ['job-site-prep-info', jobId],
    () => getJobSitePrepInfo(jobId),
    {
      //   enabled: jobId !== 'add'
    }
  );

  const {
    isLoading: isLoadingHouseInfo,
    data: houseInfo,
    refetch: refetchHouseInfo
  } = useQuery<IJobHouseInfo, Error>(['job-house-info', jobId], () => getJobHouseInfo(jobId), {
    //   enabled: jobId !== 'add'
  });

  const {
    isLoading: isLoadingUtilityMunicipalInfo,
    data: utilityMunicipalInfo,
    refetch: refetchUtilityMuncipalInfo
  } = useQuery<IUtilityMunicipalInfo, Error>(
    ['utility-municipality-info', jobId],
    () => getUtilityMunicipalInfo(jobId),
    {
      //   enabled: jobId !== 'add'
    }
  );

  const {
    isLoading: isLoadingFinanceInfo,
    data: financeInfo,
    refetch: refetchFinanceInfo
  } = useQuery<IFinanceInfo, Error>(['job-finance-info', jobId], () => getFinanceInfo(jobId));

  const {
    isLoading: isLoadingContractInfo,
    data: contractInfo,
    refetch: refetchContractInfo
  } = useQuery<IContractInfo, Error>(['job-contract-info', jobId], () => getContractInfo(jobId));

  const {
    isLoading: isLoadingContractInfoReadOnly,
    data: contractInfoReadOnly,
    refetch: refetchContractInfoReadOnly
  } = useQuery<IContractInfoReadOnly, Error>(['job-contract-info-read-only', jobId], () =>
    getContractInfoReadOnly(jobId)
  );

  useEffect(() => {
    if (window.location.href.includes('general-information')) {
      setSelectedPage(jobPages.genInfo);
    }
    if (window.location.href.includes('lot-information')) {
      setSelectedPage(jobPages.lotInfo);
    }
    if (window.location.href.includes('site-prep')) {
      setSelectedPage(jobPages.sitePrep);
    }
    if (window.location.href.includes('house-info')) {
      setSelectedPage(jobPages.houseInfo);
    }
    if (window.location.href.includes('utility-municipality-information')) {
      setSelectedPage(jobPages.utilityMunicipalInfo);
    }
    if (window.location.href.includes('finance-info')) {
      setSelectedPage(jobPages.financeInfo);
    }
    if (window.location.href.includes('contract-info')) {
      setSelectedPage(jobPages.contractInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href, jobId]);

  const getJobId = () => {
    return jobId ? jobId : 'add';
  };
  const jobLinks = useMemo(
    () =>
      [
        {
          name: 'General Info',
          url: `/development/jobs/${getJobId()}/general-information`
        },
        {
          name: 'Lot Info',
          url: `/development/jobs/${getJobId()}/lot-information`
        },
        {
          name: 'Site Prep / Landscaping',
          url: `/development/jobs/${getJobId()}/site-prep`
        },
        {
          name: 'House Info',
          url: `/development/jobs/${getJobId()}/house-information`
        },
        {
          name: 'Utility and Municipality Info',
          url: `/development/jobs/${getJobId()}/utility-municipality-information`
        },
        {
          name: 'Finance Info',
          url: `/development/jobs/${getJobId()}/finance-information`
        },
        {
          name: 'Contract Info',
          url: `/development/jobs/${getJobId()}/contract-information`
        }
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobId]
  );

  const showLoader = isLoadingGeneralInfo || isLoadingLotInfo;
  const title = showLoader
    ? 'Loading...'
    : jobId === 'add'
    ? 'Add Job'
    : `Edit Job - ${generalInfo?.jobCode}`;

  return (
    <Page title={title}>
      <>
        <PageTitleWithBack
          title={title}
          backText="Back to jobs"
          backUrl="/development/jobs"
          additionalHeaderContent={
            <Grid container sx={{ display: 'flex', alignItem: 'flex-end' }}>
              <Tooltip
                title={!generalInfo?.hasClientAssigned ? 'No matching sale available' : ''}
                placement="top"
              >
                <div className={classes.viewInSales}>
                  <SMButton
                    disabled={!generalInfo?.hasClientAssigned}
                    id="view-in-sales"
                    color="secondary"
                    variant="contained"
                    asLink
                    to={`/sales/contracting/ten-day-builds/${generalInfo?.buildId}?redirect=/sales/contracting/ten-day-builds`}
                  >
                    <span className={classes.buttonText}>View in Sales</span>
                  </SMButton>
                </div>
              </Tooltip>
              <Tooltip
                title={!generalInfo?.hasBuild ? 'No matching builds available' : ''}
                placement="top"
              >
                <div>
                  <Button variant="contained" color="secondary" disabled={!generalInfo?.hasBuild}>
                    {documentationBuildDocumentation ? (
                      <Link to={`/documentation/build-documentation/${generalInfo?.buildId}`}>
                        <span className={classes.buttonText}>View in Documentation Portal</span>
                      </Link>
                    ) : (
                      <ExternalLink
                        to={`${LegacySystem}/Portals/Documentation/Documentation.aspx?JobCode=${generalInfo?.jobCode}`}
                        target="_blank"
                      >
                        <span className={classes.buttonText}>View in Documentation Portal</span>
                      </ExternalLink>
                    )}
                  </Button>
                </div>
              </Tooltip>
            </Grid>
          }
        />
        {jobId !== 'add' && (
          <AppBar position="static" color="default" component="div" className={classes.tabBar}>
            <Tabs
              value={selectedTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_, value) => {
                if (!isDirtyForm) {
                  setSelectedTab(value);
                  history.push(value);
                } else {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    setSelectedTab(value);
                    history.push(value);
                  } else {
                    return;
                  }
                }
              }}
              variant="scrollable"
            >
              {jobLinks.map(link => {
                return (
                  <Tab
                    value={link.url}
                    key={link.name}
                    id={`${link.name}-information`}
                    aria-controls={`${link.name}-information`}
                    label={link.name}
                  />
                );
              })}
            </Tabs>
          </AppBar>
        )}
        {selectedPage === jobPages.genInfo && (
          <GeneralInformation
            setDirtyForm={setDirtyForm}
            generalInfo={generalInfo}
            isLoadingGeneralInfo={isLoadingGeneralInfo}
            refetchGeneralInfo={refetchGeneralInfo}
          />
        )}
        {selectedPage === jobPages.lotInfo && (
          <LotInformation
            setDirtyForm={setDirtyForm}
            lotInfo={lotInfo}
            isLoadingLotInfo={isLoadingLotInfo}
            refetchLotInfo={refetchLotInfo}
          />
        )}
        {selectedPage === jobPages.sitePrep && (
          <SitePrep
            setDirtyForm={setDirtyForm}
            sitePrepInfo={sitePrepInfo}
            isLoadingSitePrepInfo={isLoadingSitePrepInfo}
            refetchSitePrepInfo={refetchSitePrepInfo}
          />
        )}
        {selectedPage === jobPages.houseInfo && (
          <HouseInfo
            setDirtyForm={setDirtyForm}
            houseInfo={houseInfo}
            isLoadingHouseInfo={isLoadingHouseInfo}
            refetchHouseInfo={refetchHouseInfo}
          />
        )}
        {selectedPage === jobPages.utilityMunicipalInfo && (
          <UtilityMunicipalityInfo
            setDirtyForm={setDirtyForm}
            utilityMunicipalityInfo={utilityMunicipalInfo}
            isLoadingUtilityMunicipalityInfo={isLoadingUtilityMunicipalInfo}
            refetchUtilityMunicipalityInfo={refetchUtilityMuncipalInfo}
          />
        )}
        {selectedPage === jobPages.financeInfo && (
          <FinanceInfo
            setDirtyForm={setDirtyForm}
            financeInfo={financeInfo}
            isLoadingFinanceInfo={isLoadingFinanceInfo}
            refetchFinanceInfo={refetchFinanceInfo}
          />
        )}
        {selectedPage === jobPages.contractInfo && (
          <ContractInfo
            setDirtyForm={setDirtyForm}
            contractInfo={contractInfo}
            isLoadingContractInfo={isLoadingContractInfo}
            refetchContractInfo={refetchContractInfo}
            contractInfoReadOnly={contractInfoReadOnly}
            isLoadingContractInfoReadOnly={isLoadingContractInfoReadOnly}
            refetchContractInfoReadOnly={refetchContractInfoReadOnly}
          />
        )}
      </>
    </Page>
  );
};

const useStyles = makeStyles(theme => ({
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  buttonText: {
    color: 'white'
  },
  viewInSales: {
    marginRight: '.5rem',
    [theme.breakpoints.down(558)]: {
      paddingBottom: '4px'
    }
  }
}));
